import type { Actions, Subjects } from '@/libs/ability'

export const APP_ROUTES = {
  home: '/',
  auth: {
    login: '/login',
    forgotPassword: '/forgot-password',
    resetPassword: '/reset-password',
  },
  account: '/account',
  changePassword: '/change-password',
  leaves: '/leaves',
  workLogs: '/work-logs',
  billing: '/billing',
  pricing: '/pricing',
  roles: '/roles',
  campaigns: '/campaigns',
  integrations: '/integrations',
  loanCars: '/loan-cars',
  category: {
    base: '/categories',
    type: (categoryType: string) => `/categories/${categoryType}`,
    create: (categoryType: string) => `/categories/${categoryType}/new`,
    edit: (categoryType: string, id: string) => `/categories/${categoryType}/${id}/edit`,
    details: (categoryType: string, id: string) => `/categories/${categoryType}/${id}`,
  },
  customer: {
    base: '/customers',
    type: (customerEntityType: string) => `/customers/${customerEntityType}`,
    create: (customerEntityType: string) => `/customers/${customerEntityType}/new`,
    details: (id: string) => `/customers/${id}`,
    new: `/customers/new`,
    companyNew: `/customers/company/new`,
    companiesNew: `/customers/companies/new`,
  },
  communication: {
    base: '/communication',
    type: (templateType: string) => `/communication/${templateType}`,
    create: (templateType: string) => `/communication/${templateType}/new`,
    details: (templateType: string, id: string) => `/communication/${templateType}/${id}`,
  },
  dashboard: '/dashboard',
  booking: {
    base: '/booking',
    details: (id: string) => `/booking/${id}`,
    new: `/booking/new`,
  },
  employee: {
    base: '/employees',
    details: (id: string) => `/employees/${id}`,
    edit: (id: string) => `/employees/${id}/edit`,
    new: `/employees/new`,
  },
  rentCar: {
    base: '/rental-fleet',
    new: `/rental-fleet/new`,
    details: (id: string) => `/rental-fleet/${id}`,
  },
  service: {
    base: '/services',
    new: `/services/new`,
    edit: (id: string) => `/services/${id}/edit`,
    details: (id: string) => `/services/${id}`,
  },
  settings: {
    base: '/settings',
    branchInfo: '/settings/branch-info',
    workingHours: '/settings/working-hours',
    permissions: '/settings/permissions',
    weeklyNotes: '/settings/weekly-notes',
    stores: {
      base: '/settings/stores',
      details: (id: string) => `/settings/stores/${id}`,
      new: `/settings/stores/new`,
    },
    qualifications: {
      base: '/settings/qualifications',
      details: (type: string, id: string) => `/settings/qualifications/${id}?type=${type}`,
      new: (type: string) => `/settings/qualifications/new?type=${type}`,
    },
    staff: {
      base: '/settings/staff',
      details: (id: string) => `/settings/staff/${id}`,
    },
  },
  vehicle: {
    base: '/vehicles',
    details: (id: string) => `/vehicles/${id}`,
    new: `/vehicles/new`,
  },
  job: {
    base: '/jobs',
    details: (id: string) => `/jobs/${id}`,
    new: `/jobs/new`,
  },
  product: {
    base: '/products',
    details: (id: string) => `/products/${id}`,
    new: `/products/new`,
  },
}

type RoutePermission = {
  requiredAction: Actions
  requiredSubject: Subjects
}

const perm = (requiredAction: Actions, requiredSubject: Subjects): RoutePermission => ({
  requiredAction,
  requiredSubject,
})

export const ROUTE_PERMISSIONS = {
  customers: {
    read: perm('read', 'Customer'),
    create: perm('create', 'Customer'),
  },
  vehicles: {
    read: perm('read', 'Vehicle'),
    create: perm('create', 'Vehicle'),
  },
  services: {
    read: perm('read', 'Service'),
    create: perm('create', 'Service'),
  },
  jobs: {
    read: perm('read', 'Job'),
    create: perm('create', 'Job'),
  },
  booking: {
    read: perm('read', 'Job'),
    create: perm('create', 'Job'),
  },
  leaves: {
    read: perm('read', 'Leave'),
  },
  workLogs: {
    read: perm('read', 'WorkLog'),
  },
  billing: {
    read: perm('read', 'Billing'),
  },
  products: {
    read: perm('read', 'Product'),
    create: perm('create', 'Product'),
  },
  rentalFleet: {
    read: perm('read', 'LoanCar'),
    create: perm('create', 'LoanCar'),
  },
  campaigns: {
    read: perm('read', 'Campaign'),
  },
  categories: {
    read: perm('read', 'Category'),
    create: perm('create', 'Category'),
  },
  communication: {
    read: perm('read', 'Communication'),
    create: perm('create', 'Communication'),
  },
  settings: {
    read: perm('read', 'Settings'),
  },
  user: {
    read: perm('read', 'User'),
  },
}
